import { useEffect, useMemo, useState } from "react";
import UIkit from "uikit";
import moment from "moment";
import classnames from "classnames";
import { Image } from "uikit-react";

const DOWS = [
  { id: 1, short: "Sun", label: "Sunday" },
  { id: 2, short: "Mon", label: "Monday" },
  { id: 3, short: "Tue", label: "Tuesday" },
  { id: 4, short: "Wed", label: "Wednesday" },
  { id: 5, short: "Thu", label: "Thursday" },
  { id: 6, short: "Fri", label: "Friday" },
  { id: 7, short: "Sat", label: "Saturday" },
];

const wdFormat = "YYYY-MM-DD HH:mm:ss";
const getDayPrefix = (dow) => `2001-01-0${dow}`; // sentinel date per weekday

const makeTimeOptions = (stepMins = 15) => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMins) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
};

const timeOptionsStart = makeTimeOptions(15);
const timeOptionsEnd = [...makeTimeOptions(15), "23:59"];

// Filter end times to only show times greater than start time
const getFilteredEndTimes = (startTime) => {
  if (!startTime) return timeOptionsEnd;
  const startMinutes = moment(startTime, "HH:mm").hours() * 60 + moment(startTime, "HH:mm").minutes();
  return timeOptionsEnd.filter(t => {
    const endMinutes = moment(t, "HH:mm").hours() * 60 + moment(t, "HH:mm").minutes();
    return endMinutes > startMinutes;
  });
};

const hasTZ = (v) => /Z|[+-]\d{2}:\d{2}$/.test(String(v || "").trim());

const normalizeWallClock = (v) =>
  String(v || "")
    .trim()
    .replace("T", " ")
    .replace(/\.\d+/, "")
    .replace(/(Z|[+-]\d{2}:\d{2})$/, "")
    .trim();

const parseWd = (v) => {
  if (!v) return moment.invalid();
  const s0 = String(v).trim();
  if (hasTZ(s0)) return moment.parseZone(s0).local();
  const s = normalizeWallClock(s0);
  return moment(s, wdFormat, true);
};

const toStorage = (m) => m.format(wdFormat);

const localAt = (dateYYYYMMDD, hhmmss) =>
  moment(`${dateYYYYMMDD} ${hhmmss}`, wdFormat, true); // strict local

const hhmmToLabel = (hhmm) => moment(hhmm, "HH:mm").format("h:mma");

const uid = () => Math.random().toString(36).slice(2, 10);

export default function WorkScheduleEditor({
  open,
  onClose,
  onSave,

  user,
  schedule: initialSchedule = [],
  holidayCatalog = [],
  holidayOverrides: initialHolidayOverrides = {},

  readOnly = false,
}) {
  const [applyDays, setApplyDays] = useState([2, 3, 4, 5, 6]); // default weekdays
  const [schedule, setSchedule] = useState([]);
  const [holidayOverrides, setHolidayOverrides] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Initialize local state when opened or when user changes
  useEffect(() => {
    if (!open) return;

    const next = (initialSchedule || []).map((s) => {
      const dow = +s.day_of_week;
      const date = getDayPrefix(dow);
      
      // Backend sends HH:MM:SS format, prepend the sentinel date
      const startTime = s.start_time || "09:00:00";
      const endTime = s.end_time || "17:00:00";
      
      return {
        id: s.id || uid(),
        day_of_week: dow,
        start_time: `${date} ${startTime}`,
        end_time: `${date} ${endTime}`,
      };
    });

    setSchedule(next);
    setHolidayOverrides(initialHolidayOverrides || {});
    setApplyDays([2, 3, 4, 5, 6]);

    setTimeout(() => UIkit.update(document.getElementById("work-schedule-editor")), 0);
  }, [open, user?.user_id, user?.recipient_id]);

  const fullName = useMemo(
    () => [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "User",
    [user?.first_name, user?.last_name],
  );

  const dayRows = useMemo(() => {
    const rowsByDow = {};
    for (const d of DOWS) rowsByDow[d.id] = [];
    (schedule || []).forEach((s) => {
      if (!rowsByDow[+s.day_of_week]) rowsByDow[+s.day_of_week] = [];
      rowsByDow[+s.day_of_week].push(s);
    });

    for (const dow of Object.keys(rowsByDow)) {
      rowsByDow[dow].sort((a, b) => {
        const ma = parseWd(a.start_time);
        const mb = parseWd(b.start_time);
        return ma.valueOf() - mb.valueOf();
      });
    }

    return rowsByDow;
  }, [schedule]);

  const isDayOff = (dow) => (dayRows[dow] || []).length === 0;

  const toggleApplyDay = (dow) => {
    setApplyDays((prev) => {
      const exists = prev.includes(dow);
      return exists ? prev.filter((d) => d !== dow) : [...prev, dow].sort((a, b) => a - b);
    });
  };

  const addShift = (dow) => {
    if (readOnly) return;

    const date = getDayPrefix(dow);
    const shifts = dayRows[dow] || [];

    let start = localAt(date, "09:00:00");
    let end = localAt(date, "17:00:00");

    if (shifts.length) {
      const last = shifts[shifts.length - 1];
      const lastEnd = parseWd(last.end_time);
      if (lastEnd.isValid()) {
        start = lastEnd.clone().add(15, "minutes");
        end = start.clone().add(60, "minutes");
      }
    }

    setSchedule((prev) => [
      ...(prev || []),
      {
        id: uid(),
        day_of_week: dow,
        start_time: toStorage(start),
        end_time: toStorage(end),
      },
    ]);
  };

  const removeShift = (id) => {
    if (readOnly) return;
    setSchedule((prev) => (prev || []).filter((s) => s.id !== id));
  };

  const setShiftHHMM = (id, key, hhmm) => {
    if (readOnly) return;

    setSchedule((prev) => {
      const next = (prev || []).map((s) => ({ ...s }));
      const row = next.find((s) => s.id === id);
      if (!row) return prev;

      const dow = +row.day_of_week;
      const date = getDayPrefix(dow);

      const base = parseWd(row[key] || `${date} 00:00:00`);
      const datePart = base.isValid() ? base.format("YYYY-MM-DD") : date;

      const nextLocal = localAt(datePart, `${hhmm}:00`);

      if (key === "start_time") {
        const endLocal = parseWd(row.end_time);
        row.start_time = toStorage(nextLocal);
        if (!endLocal.isValid() || !endLocal.isAfter(nextLocal)) {
          row.end_time = toStorage(nextLocal.clone().add(60, "minutes"));
        }
      } else {
        const startLocal = parseWd(row.start_time);
        row.end_time = toStorage(nextLocal);
        if (!startLocal.isValid() || !nextLocal.isAfter(startLocal)) {
          row.start_time = toStorage(nextLocal.clone().subtract(60, "minutes"));
        }
      }

      return next;
    });
  };

  const clearDay = (dow) => {
    if (readOnly) return;
    setSchedule((prev) => (prev || []).filter((s) => +s.day_of_week !== +dow));
  };

  const setNineToFive = (dow) => {
    if (readOnly) return;
    const date = getDayPrefix(dow);
    setSchedule((prev) => {
      const remaining = (prev || []).filter((s) => +s.day_of_week !== +dow);
      return [
        ...remaining,
        {
          id: uid(),
          day_of_week: dow,
          start_time: toStorage(localAt(date, "09:00:00")),
          end_time: toStorage(localAt(date, "17:00:00")),
        },
      ];
    });
  };

  const setAllDay = (dow) => {
    if (readOnly) return;
    const date = getDayPrefix(dow);
    setSchedule((prev) => {
      const remaining = (prev || []).filter((s) => +s.day_of_week !== +dow);
      return [
        ...remaining,
        {
          id: uid(),
          day_of_week: dow,
          start_time: toStorage(localAt(date, "00:00:00")),
          end_time: toStorage(localAt(date, "23:59:00")),
        },
      ];
    });
  };

  const applyThisDayToSelected = (sourceDow) => {
    if (readOnly) return;

    const src = dayRows[sourceDow] || [];
    if (!src.length) {
      UIkit.notification("Nothing to apply: this day is Off.", "warning");
      return;
    }

    const srcPairs = src.map((s) => {
      const st = parseWd(s.start_time);
      const et = parseWd(s.end_time);
      return {
        st: st.isValid() ? st.format("HH:mm:ss") : "09:00:00",
        et: et.isValid() ? et.format("HH:mm:ss") : "17:00:00",
      };
    });

    setSchedule((prev) => {
      let next = (prev || []).filter((s) => !applyDays.includes(+s.day_of_week)); // wipe target days
      applyDays.forEach((dow) => {
        const date = getDayPrefix(dow);
        srcPairs.forEach((p) => {
          next.push({
            id: uid(),
            day_of_week: dow,
            start_time: toStorage(localAt(date, p.st)),
            end_time: toStorage(localAt(date, p.et)),
          });
        });
      });
      return next;
    });

    UIkit.notification("Applied schedule to selected days.");
  };

  const validate = () => {
    // group shifts by day_of_week
    const byDow = {};
    for (const d of DOWS) byDow[d.id] = [];

    (schedule || []).forEach((s) => {
      if (!s) return;
      byDow[+s.day_of_week]?.push(s);
    });

    for (const dowStr of Object.keys(byDow)) {
      const dow = +dowStr;
      const rows = (byDow[dow] || [])
        .map((r) => ({
          ...r,
          _start: parseWd(r.start_time),
          _end: parseWd(r.end_time),
        }))
        .sort((a, b) => a._start.valueOf() - b._start.valueOf());

      // validate each row + check overlap
      for (let i = 0; i < rows.length; i++) {
        const cur = rows[i];

        if (!cur._start.isValid() || !cur._end.isValid()) {
          return `Invalid time on ${DOWS.find((d) => d.id === dow)?.label || "day"}.`;
        }

        if (!cur._end.isAfter(cur._start)) {
          return `End time must be after start time on ${
            DOWS.find((d) => d.id === dow)?.label || "day"
          }.`;
        }

        if (i > 0) {
          const prev = rows[i - 1];

          // overlap if prev end is AFTER current start
          if (prev._end.isAfter(cur._start)) {
            const dayName = DOWS.find((d) => d.id === dow)?.label || "day";
            const prevLabel = `${hhmmToLabel(prev._start.format("HH:mm"))}–${hhmmToLabel(
              prev._end.format("HH:mm"),
            )}`;
            const curLabel = `${hhmmToLabel(cur._start.format("HH:mm"))}–${hhmmToLabel(
              cur._end.format("HH:mm"),
            )}`;

            return `Overlapping shifts on ${dayName}: ${prevLabel} overlaps ${curLabel}.`;
          }
        }
      }
    }

    return null;
  };

  const handleSave = async () => {
    if (readOnly) return;

    const err = validate();
    if (err) {
      UIkit.notification(err, "error");
      return;
    }

    setIsSaving(true);
    try {
      const payloadSchedule = (schedule || []).map((s) => {
        const payload = {
          day_of_week: +s.day_of_week,
          start_time: normalizeWallClock(s.start_time).split(' ')[1] || normalizeWallClock(s.start_time),
          end_time: normalizeWallClock(s.end_time).split(' ')[1] || normalizeWallClock(s.end_time),
        };
        
        // Only include id if it's numeric (from backend database)
        // Client-generated ids from uid() are excluded
        if (typeof s.id === 'number' || (typeof s.id === 'string' && /^\d+$/.test(s.id))) {
          payload.id = +s.id;
        }
        
        return payload;
      });

      await onSave?.({
        schedule: payloadSchedule,
        holiday_overrides: holidayOverrides || {},
      });

      // Parent handles success notification and closing
      onClose?.();
    } catch (e) {
      console.error("WorkScheduleEditor save failed", e);
    } finally {
      setIsSaving(false);
    }
  };

  const close = () => {
    onClose?.();
    UIkit.offcanvas("#work-schedule-editor")?.hide?.();
  };

  // Holidays
  const holidaysForUI = useMemo(() => {
    const list = Array.isArray(holidayCatalog) ? holidayCatalog : [];
    return list
      .map((h) => ({
        ...h,
        date: String(h.date || "").slice(0, 10),
        dow: moment(String(h.date || "").slice(0, 10), "YYYY-MM-DD", true).isValid()
          ? moment(String(h.date || "").slice(0, 10), "YYYY-MM-DD").format("ddd")
          : "",
      }))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [holidayCatalog]);

  const isWorkingOnHoliday = (iso) => !!holidayOverrides?.[iso]?.working;

  const toggleHolidayWorking = (iso) => {
    if (readOnly) return;
    setHolidayOverrides((prev) => {
      const next = { ...(prev || {}) };
      const cur = !!next[iso]?.working;
      next[iso] = { working: !cur };
      return next;
    });
  };

  const shiftsSummaryHere = useMemo(() => {
    return (shifts = []) => {
      if (!shifts.length) return "(Off)";
      return shifts
        .map((s) => {
          const st = parseWd(s.start_time).format("HH:mm");
          const et = parseWd(s.end_time).format("HH:mm");
          return `${hhmmToLabel(st)}–${hhmmToLabel(et)}`;
        })
        .join(", ");
    };
  }, []);

  // Reactive overlap detection
  const overlapError = useMemo(() => {
    const byDow = {};
    for (const d of DOWS) byDow[d.id] = [];
    (schedule || []).forEach((s) => {
      if (s) byDow[+s.day_of_week]?.push(s);
    });

    for (const dowStr of Object.keys(byDow)) {
      const dow = +dowStr;
      const rows = (byDow[dow] || [])
        .map((r) => ({
          ...r,
          _start: parseWd(r.start_time),
          _end: parseWd(r.end_time),
        }))
        .sort((a, b) => a._start.valueOf() - b._start.valueOf());

      for (let i = 1; i < rows.length; i++) {
        const prev = rows[i - 1];
        const cur = rows[i];
        if (!prev._start.isValid() || !prev._end.isValid()) continue;
        if (!cur._start.isValid() || !cur._end.isValid()) continue;

        if (prev._end.isAfter(cur._start)) {
          const dayName = DOWS.find((d) => d.id === dow)?.label || "day";
          const prevLabel = `${hhmmToLabel(prev._start.format("HH:mm"))}–${hhmmToLabel(prev._end.format("HH:mm"))}`;
          const curLabel = `${hhmmToLabel(cur._start.format("HH:mm"))}–${hhmmToLabel(cur._end.format("HH:mm"))}`;
          return `Overlapping shifts on ${dayName}: ${prevLabel} overlaps ${curLabel}.`;
        }
      }
    }

    return null;
  }, [schedule]);

  if (!open) return null;

  return (
    <div
      id="work-schedule-editor"
      className="uk-offcanvas hms-modals"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar" style={{ width: "720px", maxWidth: "95vw" }}>
        <button
          className="uk-offcanvas-close uk-close-large"
          type="button"
          data-uk-close
          onClick={close}
        />

        <div className="uk-modal-header">
          <h2 className="uk-modal-title">Work Schedule</h2>
          <div className="uk-text-meta">
            {fullName}
            {user?.email ? ` · ${user.email}` : ""}
          </div>
        </div>

        <div className="uk-modal-body">
          <div className="uk-margin-small">
            <div className="uk-text-meta uk-margin-small-top uk-margin-small-bottom">
              Add multiple shifts per day. Holidays are off by default.
            </div>

            {DOWS.map((d, idx) => {
              const shifts = dayRows[d.id] || [];
              const off = shifts.length === 0;

              return (
                <div key={d.id} className="uk-margin-small work-day-row">

                  <div className="work-day-header">
                    <div className="work-day-title">
                      <div className="uk-text-bold">{d.label}</div>

                      {off ? (
                        <div className="uk-text-muted">(Off)</div>
                      ) : (
                        <div className="uk-text-meta work-day-summary">
                          {shifts
                            .map((s) => {
                              const st = parseWd(s.start_time).isValid()
                                ? parseWd(s.start_time).format("HH:mm")
                                : "09:00";
                              const et = parseWd(s.end_time).isValid()
                                ? parseWd(s.end_time).format("HH:mm")
                                : "17:00";
                              return `${hhmmToLabel(st)}–${hhmmToLabel(et)}`;
                            })
                            .join(", ")}
                        </div>
                      )}
                    </div>

                    <div className="work-day-actions">
                      <button
                        type="button"
                        className="download"
                        disabled={readOnly}
                        onClick={() => addShift(d.id)}
                        uk-tooltip="Add Shift"
                      >
                        <Image src="/images/add-shift.svg" />
                      </button>

                      <button
                        type="button"
                        className="download"
                        disabled={readOnly}
                        onClick={() => setNineToFive(d.id)}
                        uk-tooltip="9 to 5"
                      >
                        <Image src="/images/nine-to-five.svg" />
                      </button>

                      <button
                        type="button"
                        className="download"
                        disabled={readOnly}
                        onClick={() => setAllDay(d.id)}
                        uk-tooltip="All Day"
                      >
                        <Image src="/images/24-hours.svg" />
                      </button>

                      <button
                        type="button"
                        className="download"
                        disabled={readOnly}
                        onClick={() => applyThisDayToSelected(d.id)}
                        uk-tooltip="Apply to All"
                      >
                        <Image src="/images/copy-schedule.svg" />
                      </button>

                      <button
                        type="button"
                        className="download"
                        disabled={readOnly}
                        onClick={() => clearDay(d.id)}
                        uk-tooltip="Day Off"
                      >
                        <Image src="/images/day-off.svg" />
                      </button>
                    </div>
                  </div>

                  {/* Shifts list */}
                  <div className="uk-margin-small-top">
                    {off ? (
                      <div className="uk-padding-small" style={{ paddingLeft: 0 }}>
                        No shifts.
                      </div>
                    ) : (
                      <div className="uk-grid-small" data-uk-grid>
                        {shifts.map((s, shiftIdx) => {
                          const st = parseWd(s.start_time).isValid()
                            ? parseWd(s.start_time).format("HH:mm")
                            : "09:00";
                          const et = parseWd(s.end_time).isValid()
                            ? parseWd(s.end_time).format("HH:mm")
                            : "17:00";

                          return (
                            <div key={s.id} className="uk-width-1-1">
                              <div
                                className="uk-flex uk-flex-middle"
                                style={{ gap: 12, flexWrap: "wrap" }}
                              >
                                {/* Start */}
                                <div>
                                  <label className="uk-form-label uk-margin-small-right">Start</label>
                                  <div className="uk-inline">
                                    <div key={`st-form-${s.id}-${st}`} uk-form-custom="target: > * > span:first-child">
                                      <select
                                        value={st}
                                        disabled={readOnly}
                                        onChange={(e) => setShiftHHMM(s.id, "start_time", e.target.value)}
                                      >
                                        {timeOptionsStart.map((t) => (
                                          <option key={`st-${s.id}-${t}`} value={t}>
                                            {hhmmToLabel(t)}
                                          </option>
                                        ))}
                                      </select>

                                      <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                                        <span></span>
                                        <span uk-icon="icon: chevron-down"></span>
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* End */}
                                <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
                                  <div>
                                    <label className="uk-form-label uk-margin-small-right">End</label>
                                    <div className="uk-inline">
                                      <div key={`et-form-${s.id}-${st}-${et}`} uk-form-custom="target: > * > span:first-child">
                                        <select
                                          value={et}
                                          disabled={readOnly}
                                          onChange={(e) => setShiftHHMM(s.id, "end_time", e.target.value)}
                                        >
                                          {getFilteredEndTimes(st).map((t) => (
                                            <option key={`et-${s.id}-${t}`} value={t}>
                                              {hhmmToLabel(t)}
                                            </option>
                                          ))}
                                        </select>

                                        <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                                          <span></span>
                                          <span uk-icon="icon: chevron-down"></span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Delete button - only for shifts after the first */}
                                  {shiftIdx > 0 && (
                                    <button
                                      type="button"
                                      className="download uk-text-muted"
                                      disabled={readOnly}
                                      onClick={() => removeShift(s.id)}
                                      uk-tooltip="Delete Shift"
                                      style={{ opacity: 0.6 }}
                                    >
                                      <Image src="/images/ops-delete.svg" style={{ width: '16px', height: '16px' }} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Divider between days */}
                  {idx < DOWS.length - 1 && <hr className="uk-margin-small" />}
                </div>
              );
            })}
          </div>

          {overlapError && (
            <div className="uk-margin-small" style={{ color: "#f0506e" }}>
              <span className="uk-text-small">⚠ {overlapError}</span>
            </div>
          )}

          <div className="uk-margin-top uk-flex uk-flex-left">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="button"
              onClick={handleSave}
              disabled={readOnly || isSaving || !!overlapError}
            >
              {isSaving ? (
                <>
                  <span uk-spinner="ratio: 0.6" className="uk-margin-small-right" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
            <button
              className="uk-button hms-btn uk-offcanvas-close"
              type="button"
              onClick={close}
              disabled={isSaving}
            >
              Cancel
            </button>            
          </div>
        </div>
      </div>
    </div>
  );
}